import { Dialog, Transition } from "@headlessui/react";
import { Address } from "@helium/crypto";
import { XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import { find, values } from "lodash";
import { stringifyUrl } from "query-string";
import Tooltip from "rc-tooltip";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory } from "react-router";
import AlertMessage from "../../../components/AlertMessage";
import CenteredLoading from "../../../components/CenteredLoading";
import Checkbox from "../../../components/forms/Checkbox";
import DropdownSearch from "../../../components/forms/DropdownSearch";
import FormProvider from "../../../components/forms/FormProvider";
import HandleFieldChange from "../../../components/forms/HandleFieldChange";
import Input from "../../../components/forms/Input";
import InfoIcon from "../../../components/icons/InfoIcon";
import UpgradeHotspotsModal from "../../../components/modals/UpgradeHotspotsModal";
import { useBillingPlans } from "../../../context/billingPlans";
import {
  convertEtlHotspotSearchResponse,
  fetchWalletHotspots,
  HotspotsSearchResponse
} from "../../../context/hotspots/HotspotsProvider";
import { ModalConfig } from "../../../context/toast/ToastProvider";
import { useWallets } from "../../../context/wallets";
import { useWorkspaces } from "../../../context/workspaces";
import { avatarsApi } from "../../../utils/constants";
import { formatHotspotName } from "../../../utils/hotspotUtils";
import { h3ToCenterChildMemoized } from "../../../utils/memoizedMethods";
import {
  EtlHotspot,
  Hotspot,
  PaymentStep,
  PlanType
} from "../../../utils/types";
import useMapCenter from "../../../utils/useMapCenter";

interface Props {
  open: boolean;
  toggle: () => void;
  walletAddress?: string;
  hotspot?: EtlHotspot;
}

const AddWalletSidebar: React.FC<Props> = ({
  open,
  toggle,
  walletAddress,
  hotspot
}) => {
  const { wallets, addWallet } = useWallets();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const centerInH3HexRes4 = useMapCenter((state) => state.centerInH3HexRes4);
  const [walletHotspotCountMap, setWalletHotspotCountMap] = useState<{
    [walletAddress: string]: number;
  }>({});
  const { currentWorkspace } = useWorkspaces();
  const { currentPlan, setPaymentStep, setNewPlan } = useBillingPlans();
  const lastFetchedWalletAddress = useRef<string | null>(null);
  const [loadingHotspotCount, setLoadingHotspotCount] = useState(false);
  const [currentOwnerExceededLimit, setCurrentOwnerExceededLimit] = useState(0);
  const [upgradeHotspotsConfig, setUpgradeHotspotsConfig] =
    useState<ModalConfig>({
      open: false
    });
  const history = useHistory();

  const initialValues = useMemo(() => {
    return {
      walletAddress: walletAddress ?? "",
      hotspot,
      tracked: true
    };
  }, [walletAddress, hotspot]);

  const handleCloseSidebar = useCallback(() => {
    lastFetchedWalletAddress.current = null;
    setCurrentOwnerExceededLimit(0);
    toggle();
  }, [toggle]);

  const onSubmit = useCallback(
    async (
      { walletAddress, tracked }: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      if (
        walletHotspotCountMap[walletAddress] +
          (currentWorkspace?.counts.hotspot || 0) >
          Number(currentWorkspace?.active_plan?.allowed_hotspots) &&
        tracked
      ) {
        setCurrentOwnerExceededLimit(walletHotspotCountMap[walletAddress]);

        return;
      }

      const apiResponse = await addWallet(walletAddress, { tracked });

      if (apiResponse?.status) {
        handleCloseSidebar();
      } else {
        setErrorMessage(apiResponse?.message || "Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [
      addWallet,
      currentWorkspace?.active_plan?.allowed_hotspots,
      currentWorkspace?.counts.hotspot,
      handleCloseSidebar,
      walletHotspotCountMap
    ]
  );

  const handleFetchWalletHotspots = useCallback(
    async (walletAddress: string, tracked?: boolean) => {
      setLoadingHotspotCount(true);

      const walletHotspots = await fetchWalletHotspots(walletAddress);

      setLoadingHotspotCount(false);

      if (
        (walletHotspots?.length || 0) +
          (currentWorkspace?.counts.hotspot || 0) >
          Number(currentWorkspace?.active_plan?.allowed_hotspots) &&
        tracked
      ) {
        setCurrentOwnerExceededLimit(walletHotspots?.length || 1);
      }

      setWalletHotspotCountMap((prevWalletHotspotCountMap) => ({
        ...prevWalletHotspotCountMap,
        [walletAddress]: (walletHotspots || []).length
      }));
    },
    [
      currentWorkspace?.active_plan?.allowed_hotspots,
      currentWorkspace?.counts.hotspot
    ]
  );

  useEffect(() => {
    if (walletAddress && lastFetchedWalletAddress.current !== walletAddress) {
      handleFetchWalletHotspots(walletAddress, true);
      lastFetchedWalletAddress.current = walletAddress;
    }
  }, [handleFetchWalletHotspots, walletAddress]);

  const fetchHotspots = useCallback(
    async (searchInput: string | undefined) => {
      const findHotspotsUrl = stringifyUrl({
        url: `${process.env.REACT_APP_ETL_API}/hotspots/search-lean`,
        query: {
          name: searchInput || "",
          limit: 5,
          ...(centerInH3HexRes4
            ? {
                proximity_hex: h3ToCenterChildMemoized(centerInH3HexRes4, 12)
              }
            : {})
        }
      });

      return fetch(findHotspotsUrl)
        .then((res) => res.json())
        .then(
          (res: HotspotsSearchResponse) =>
            convertEtlHotspotSearchResponse(res.data) || []
        );
    },
    [centerInH3HexRes4]
  );

  const validateHotspotField = useCallback(
    async (hotspot: Hotspot | undefined) => {
      if (!hotspot) {
        return;
      }

      const walletIsAlreadyImported = !!find(values(wallets), {
        address: hotspot.owner
      });

      if (walletIsAlreadyImported) {
        return "This hotspot's wallet was already added to your workspace.";
      }
    },
    [wallets]
  );

  const validateWalletAddressField = useCallback(
    async (walletAddress: string, tracked: boolean) => {
      if (!walletAddress) {
        return "Required";
      }

      const walletIsAlreadyImported = !!find(values(wallets), {
        address: walletAddress
      });

      if (walletIsAlreadyImported) {
        return "This wallet was already added to your workspace.";
      }

      if (!Address.isValid(walletAddress)) {
        return "Please provide a valid wallet address";
      }

      if (lastFetchedWalletAddress.current !== walletAddress) {
        handleFetchWalletHotspots(walletAddress, tracked);
        lastFetchedWalletAddress.current = walletAddress;
      }
    },
    [handleFetchWalletHotspots, wallets]
  );

  const trackHotspotsLabel = useCallback(
    (walletAddress: string | null) => {
      const walletHotspotsCount =
        walletHotspotCountMap[walletAddress || ""] || 0;

      if (walletHotspotsCount === 0) {
        return "Track wallet hotspots";
      }

      if (walletHotspotsCount === 1) {
        return "Track 1 wallet hotspot";
      }

      return `Track all ${walletHotspotsCount} wallet hotspots`;
    },
    [walletHotspotCountMap]
  );

  const limitExceededAlertMessage = useMemo(() => {
    if (!currentWorkspace) return;

    const allowedHotspots = Number(
      currentWorkspace.active_plan.allowed_hotspots
    );
    const hotspotCounts = currentWorkspace.counts.hotspot;
    const walletHotspotsCount =
      walletHotspotCountMap[lastFetchedWalletAddress.current || ""];

    if (
      walletHotspotsCount + hotspotCounts < allowedHotspots ||
      currentOwnerExceededLimit === 0
    ) {
      return null;
    }

    return (
      <AlertMessage
        type="warning"
        title={
          <span>
            This workspace has the {currentPlan?.name} plan which is limited to{" "}
            {allowedHotspots} hotspots. You are trying to add a new hotspot
            resulting in {hotspotCounts + walletHotspotsCount} hotspots, which
            is not allowed. Please{" "}
            <b
              className="font-bold dark:text-hotspotty-300 hover:underline cursor-pointer"
              onClick={() => {
                if (currentPlan?.type !== PlanType.community) {
                  setUpgradeHotspotsConfig({ open: true });
                  setNewPlan((prevState) => ({
                    ...prevState,
                    hotspotAmount: allowedHotspots + currentOwnerExceededLimit
                  }));

                  return;
                }

                history.push("/pricing");
                handleCloseSidebar();
                setPaymentStep(PaymentStep.plans);
              }}
            >
              increase your hotspot limit
            </b>{" "}
            or remove hotspots you no longer need.
          </span>
        }
      />
    );
  }, [
    currentWorkspace,
    walletHotspotCountMap,
    currentOwnerExceededLimit,
    currentPlan?.name,
    currentPlan?.type,
    history,
    handleCloseSidebar,
    setPaymentStep,
    setNewPlan
  ]);

  const checkCanshow = useCallback(() => {
    if (open || upgradeHotspotsConfig.open) return true;

    return false;
  }, [open, upgradeHotspotsConfig.open]);

  return (
    <>
      <UpgradeHotspotsModal
        config={upgradeHotspotsConfig}
        setConfig={setUpgradeHotspotsConfig}
      />

      <Transition.Root show={checkCanshow()} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 z-20 overflow-hidden"
          open={checkCanshow()}
          onClose={() => {
            if (upgradeHotspotsConfig.open) return;

            handleCloseSidebar();
          }}
        >
          <div className="absolute inset-0 overflow-hidden">
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
              <div
                className="absolute inset-0"
                onClick={() => {
                  if (upgradeHotspotsConfig.open) return;

                  handleCloseSidebar();
                }}
              />
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="relative w-screen max-w-md h-full flex flex-col">
                  <div className="flex-shrink-0 py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                    <div className="flex items-center justify-between">
                      <Dialog.Title className="text-lg font-medium text-white">
                        Add wallet
                      </Dialog.Title>
                      <div className="ml-3 h-7 flex items-center">
                        <button
                          type="button"
                          className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                          onClick={handleCloseSidebar}
                        >
                          <span className="sr-only">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <FormProvider
                    classnames={{ wrapper: "flex-1" }}
                    onCancel={handleCloseSidebar}
                    error={errorMessage}
                    disabled={
                      !!limitExceededAlertMessage || loadingHotspotCount
                    }
                    formikProps={{
                      onSubmit,
                      initialValues
                    }}
                    orderedFieldNames={ORDERED_FIELD_NAMES}
                  >
                    {({
                      isSubmitting,
                      setFieldValue,
                      values
                    }: FormikProps<FormValues>) => {
                      return (
                        <div className="flex-1 h-0 overflow-y-auto">
                          <div className="flex-1 flex flex-col justify-between">
                            <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                              <div className="space-y-6 pt-6 pb-5">
                                {limitExceededAlertMessage}

                                <Field
                                  id="wallet_search"
                                  name="walletAddress"
                                  label="Wallet address"
                                  placeholder="54296bk...6EXZon"
                                  type="text"
                                  component={Input}
                                  disabled={isSubmitting}
                                  validate={(address: string) =>
                                    validateWalletAddressField(
                                      address,
                                      values.tracked
                                    )
                                  }
                                />

                                <Field
                                  label="Find wallet by hotspot"
                                  name="hotspot"
                                  placeholder="Search a hotspot"
                                  disabled={isSubmitting}
                                  getImageUrl={(hotspot: EtlHotspot) =>
                                    stringifyUrl({
                                      url: `${avatarsApi}/bottts/${hotspot.id}.svg`,
                                      query: { w: 24, h: 24 }
                                    })
                                  }
                                  fetchItems={fetchHotspots}
                                  itemToString={formatHotspotName}
                                  component={DropdownSearch}
                                  validate={validateHotspotField}
                                />

                                <HandleFieldChange<FormValues>
                                  name="hotspot"
                                  onChange={(
                                    selectedHotspot: EtlHotspot | undefined
                                  ) => {
                                    if (
                                      selectedHotspot?.owner &&
                                      values.walletAddress !==
                                        selectedHotspot.owner
                                    ) {
                                      setFieldValue(
                                        "walletAddress",
                                        selectedHotspot.owner
                                      );
                                    } else if (
                                      !selectedHotspot?.owner &&
                                      values.walletAddress
                                    ) {
                                      setFieldValue("walletAddress", "");
                                    }
                                  }}
                                />

                                <Field
                                  label={
                                    <span className="flex items-center">
                                      {loadingHotspotCount && (
                                        <CenteredLoading className="w-4 h-4 mr-1" />
                                      )}
                                      {trackHotspotsLabel(values.walletAddress)}{" "}
                                      <Tooltip
                                        placement="top"
                                        overlay="Hotspots that belong to this wallet will be automatically added to your workspace, including new hotspots in the future."
                                      >
                                        <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                                      </Tooltip>
                                    </span>
                                  }
                                  name="tracked"
                                  disabled={isSubmitting}
                                  type="checkbox"
                                  component={Checkbox}
                                />

                                <HandleFieldChange<FormValues>
                                  name="tracked"
                                  onChange={() => {
                                    if (!values.tracked) {
                                      setCurrentOwnerExceededLimit(0);
                                    }

                                    if (
                                      values.tracked &&
                                      walletHotspotCountMap[
                                        values.walletAddress
                                      ] +
                                        (currentWorkspace?.counts.hotspot ||
                                          0) >
                                        Number(
                                          currentWorkspace?.active_plan
                                            ?.allowed_hotspots
                                        )
                                    ) {
                                      setCurrentOwnerExceededLimit(
                                        walletHotspotCountMap[
                                          values.walletAddress
                                        ]
                                      );
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  </FormProvider>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default AddWalletSidebar;

//
// Utils
//

interface FormValues {
  walletAddress: string;
  hotspot: EtlHotspot;
  tracked: boolean;
}

const ORDERED_FIELD_NAMES = ["walletAddress", "hotspot", "tracked"];
